import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@helpers/translate";
import axios from "@helpers/axios/private.axios";

import CardIcon from "@assets/icons/CardIcon";
import CruptoCurrencyIcon from "@assets/icons/CruptoCurrencyIcon";
import PopularIcon from "@assets/icons/PopularIcon";
import { MethodIcon, getMethodLogo } from "../../../components/MethodIcon";

import "./index.sass";

const getId = (method, index) =>
  String(method?.method_key ?? method?.slug ?? method?.id ?? index);

const normalizeGateway = (gateway) => ({
  ...gateway,
  endpoint: `/gateway/${gateway.id}/deposit`,
});

const PaymentMethodIcon = ({ method }) => {
  const isImageLogo = typeof getMethodLogo(method) === "string";

  return (
    <MethodIcon
      method={method}
      wrapClassName={
        isImageLogo
          ? "payment-methods__icon-wrap"
          : "payment-methods__svg-icon-wrap"
      }
      className={
        isImageLogo ? "payment-methods__icon" : "payment-methods__svg-icon"
      }
    />
  );
};

const PaymentMethods = ({ searchQuery = "", onSelect }) => {
  const { __ } = useTranslation();

  const [fiatMethods, setFiatMethods] = useState([]);
  const [cryptoMethods, setCryptoMethods] = useState([]);

  const getGateways = async () => {
    try {
      const [fiatResponse, cryptoResponse] = await Promise.all([
        axios.get("/gateway/fiat"),
        axios.get("/gateway/crypto"),
      ]);

      setFiatMethods(
        (fiatResponse?.data?.data || []).map(normalizeGateway),
      );

      setCryptoMethods(
        (cryptoResponse?.data?.data || []).map(normalizeGateway),
      );
    } catch (error) {
      console.log("Gateways loading error:", error);
    }
  };

  useEffect(() => {
    getGateways();
  }, []);

  const popularMethodKeys = [
    "visa_uah",
    "usdt_trc20",
    "btc",
    "usdc_bep20",
  ];

  const search = searchQuery.trim().toLowerCase();

  const filterMethods = (methods) => {
    if (!search) return methods;

    return methods.filter((method) => {
      const title = method.title?.toLowerCase();
      const slug = method.slug?.toLowerCase();
      const type = method.type?.toLowerCase();
      const network = method.network?.toLowerCase();
      const asset = method.asset?.toLowerCase();
      const methodKey = method.method_key?.toLowerCase();

      return (
        title?.includes(search) ||
        slug?.includes(search) ||
        type?.includes(search) ||
        network?.includes(search) ||
        asset?.includes(search) ||
        methodKey?.includes(search)
      );
    });
  };

  const popularMethods = useMemo(() => {
    const methods = [...fiatMethods, ...cryptoMethods].filter((method) =>
      popularMethodKeys.includes(method.method_key),
    );

    return filterMethods(methods);
  }, [fiatMethods, cryptoMethods, search]);

  const filteredFiatMethods = useMemo(() => {
    return filterMethods(fiatMethods);
  }, [fiatMethods, search]);

  const filteredCryptoMethods = useMemo(() => {
    return filterMethods(cryptoMethods);
  }, [cryptoMethods, search]);

  const sections = [
    {
      key: "popular",
      title: __("wallet.deposit.popular"),
      Icon: PopularIcon,
      methods: popularMethods,
    },
    {
      key: "cards",
      title: __("wallet.deposit.cards"),
      Icon: CardIcon,
      methods: filteredFiatMethods,
    },
    {
      key: "crypto",
      title: __("common.cryptocurrency"),
      Icon: CruptoCurrencyIcon,
      methods: filteredCryptoMethods,
    },
  ];

  const hasMethods = sections.some((section) => section.methods.length);

  const getProcessingTime = (method) => {
    if (method.processing_time_label === "instant") {
      return __("wallet.deposit.instant");
    }

    const minutes = Number(method.processing_time_minutes);

    if (method.processing_time_minutes != null && minutes > 0) {
      return `${minutes} ${__("wallet.deposit.minutes_short")}`;
    }

    return method.processing_time_label;
  };

  const handleSelect = (method, sectionKey) => {
    const logo = getMethodLogo(method);

    const processingTime = getProcessingTime(method);

    onSelect?.({
      id: method.id,
      slug: method.slug,
      method_key: method.method_key,
      title: method.title,
      type: method.type,
      kind: method.type,
      endpoint: method.endpoint,
      icon: logo,
      logo,
      asset: method.asset,
      network: method.network,
      commission: method.comission ?? method.commission ?? null,
      minDepositAmount: method.deposit_min ?? null,
      processingTime,
      processingTimeMinutes: method.processing_time_minutes,
      method,
      sectionKey,
    });
  };

  // Тимчасово вимикаємо методи Visa/Mastercard,
  // оскільки їхній флоу ще не реалізований.
  const isMethodDisabled = (method) => {
    return (
      method.method_key === "visa_uah" ||
      method.method_key === "visa_usd"
    );
  };

  return (
    <div className="payment-methods">
      {!hasMethods && (
        <p className="payment-methods__empty">
          {__("wallet.deposit.no_payment_methods_found")}
        </p>
      )}

      {sections.map((section) => {
        if (!section.methods.length) return null;

        const SectionIcon = section.Icon;

        return (
          <div key={section.key} className="payment-methods__section">
            <div className="payment-methods__title">
              <SectionIcon />

              <h3>{section.title}</h3>
            </div>

            <ul className="payment-methods__list">
              {section.methods.map((method, index) => {
                const id = getId(method, index);
                const methodKey = `${section.key}-${id}`;
                const disabled = isMethodDisabled(method);

                const processingTime = getProcessingTime(method);

                return (
                  <li
                    key={methodKey}
                    className={`payment-methods__item ${
                      disabled ? "disabled" : ""
                    }`}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onClick={() => {
                      if (disabled) return;

                      handleSelect(method, section.key);
                    }}
                  >
                    <div className="payment-methods__item-top">
                      <PaymentMethodIcon method={method} />

                      <span className="payment-methods__name">
                        {method.title}
                      </span>
                    </div>

                    <div className="payment-methods__item-bottom">
                      <span className="payment-methods__min">
                        <p>
                          {__("wallet.deposit.min")}: ${method.deposit_min}
                        </p>
                      </span>

                      <span className="payment-methods__time">
                        <p>{processingTime || "—"}</p>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default PaymentMethods;
